import { useEffect, useMemo, useState } from 'react';
import { useUnit } from 'effector-react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { $countryProvincesStore, $countriesStore, $recipientMandatoryFieldsStore, $transferCurrenciesStore } from '@lib/store';
import { useDebounce } from '@shared/hooks';
import { cn, IbanUtils, PaletteUtils, ValidateUtils } from '@shared/utils';
import { PaymentApi, TransferRecipientAccountTypeEnum } from '@shared/api';
import type { PaymentFormData } from './paymentFormBlock';
import { BaseInput, Select, Text } from '@shared/widgets';

export interface AccountFormData {
  iban: string;
  currency: string;
  bankName: string;
  bankCountry: string;
  accountNumber: string;
  bic: string;
  accountType?: TransferRecipientAccountTypeEnum; // insi_accounttype
  bankProvince: string; // insi_bankprovince
  bankAddress: string; // insi_bankaddress
  bankCity: string; // insi_bankcity
  branchCode: string; // insi_branchcode
}

interface AccountFormBlockProps {
  className?: string;
}

export const AccountFormBlock = (props: AccountFormBlockProps) => {
  const mandatoryFields = useUnit($recipientMandatoryFieldsStore.store);
  const countryStore = useUnit($countriesStore.store);
  const transferCurrencies = useUnit($transferCurrenciesStore.store);
  const countryProvincesStore = useUnit($countryProvincesStore.store);

  const [ibanError, setIbanError] = useState<string>('');

  const { t } = useTranslation('payments');

  const { control, watch, formState, trigger, getValues, setValue, setError, clearErrors } = useFormContext<AccountFormData & PaymentFormData>();
  const iban = watch('iban');
  const accountNumber = watch('accountNumber');
  const bankCountry = watch('bankCountry');

  useEffect(() => {
    if (iban) trigger('accountNumber');
    else if (formState.touchedFields.iban) {
      trigger('iban');
      trigger('accountNumber');
    }
  }, [iban]);
  useEffect(() => {
    if (accountNumber) {
      if (!iban) setIbanError('');
      trigger('iban');
    }
  }, [accountNumber]);

  const debouncedIban = useDebounce(iban);
  useEffect(() => {
    if (!debouncedIban) return;
    (async () => {
      setValue('bankCountry', '');
      setValue('bic', '');
      setValue('bankName', '');
      setIbanError('');

      const ibanValidation = (await PaymentApi.validateIban(debouncedIban)).data!;

      if (ibanValidation.isValid) {
        const countryCode = IbanUtils.getCountry(debouncedIban);
        const selectedCountry = countryStore.data.find((e) => e.twoLetterCode === countryCode);

        if (selectedCountry?.twoLetterCode !== getValues('bankCountry'))
          setValue('bankCountry', selectedCountry!.twoLetterCode, { shouldValidate: true });
        if (ibanValidation.bic !== undefined) setValue('bic', `${ibanValidation.bic}${ibanValidation.branchBic || ''}`, { shouldValidate: true });
        if (ibanValidation.bankName) setValue('bankName', ibanValidation.bankName, { shouldValidate: true });

        clearErrors('iban');
      } else {
        setError('iban', { message: t('errors.form.iban.invalidFormat', { ns: 'common' }) });
        setIbanError(t('errors.form.iban.invalidFormat', { ns: 'common' }));
      }
    })();
  }, [debouncedIban]);

  const selectedBankProvinces = bankCountry ? (bankCountry in countryProvincesStore.data ? countryProvincesStore.data[bankCountry] : []) : [];
  const countryOptions = useMemo(() => countryStore.data.map((country) => ({ label: country.name, value: country.twoLetterCode })), [countryStore]);
  const accountTypeOptions = useMemo(
    () =>
      Object.entries(TransferRecipientAccountTypeEnum).map(([, value]) => ({
        label: t(`transferRecipientAccountType.${value.toLowerCase()}`, { ns: 'common' }),
        value,
      })),
    [t],
  );
  return (
    <div className={cn('flex flex-col gap-2', props.className)}>
      <Text className={cn(PaletteUtils.isVcfcPalette && 'dark:text-white/50')}>{t('form.titles.accountDetails')}</Text>
      <Controller
        name='iban'
        control={control}
        rules={{
          required: !accountNumber ? t('errors.form.required', { ns: 'common' }) : false,
          maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
          pattern: {
            value: ValidateUtils.alphaNumericRegex,
            message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('form.placeholders.ibanNumber') }),
          },
          validate: () => {
            if (ibanError) return ibanError;
            return true;
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <BaseInput
            {...field}
            label={t('form.placeholders.ibanNumber')}
            placeholder={t('form.placeholders.ibanNumber')}
            noSpaces
            required={!accountNumber}
            error={error?.message}
            onChange={(value) => {
              if (!value) setIbanError('');
              field.onChange(value);
            }}
            onPaste={(e) => {
              e.preventDefault();
              field.onChange(e.clipboardData.getData('text').replace(/\s+/g, '').trim());
            }}
          />
        )}
      />
      <Controller
        name='currency'
        control={control}
        rules={{ required: t('errors.form.required', { ns: 'common' }) }}
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Select
            buttonClassName='bg-transparent'
            label={t('form.placeholders.currency')}
            enableSearch
            nullValue={{ isEnable: true, text: t('form.placeholders.currency') }}
            items={transferCurrencies.data}
            required
            error={error?.message}
            value={value}
            onChange={onChange}
          />
        )}
      />
      <Controller
        name='bankName'
        control={control}
        rules={{
          required: t('errors.form.required', { ns: 'common' }),
          maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
          pattern: {
            value: ValidateUtils.alphaNumericRegex,
            message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('form.placeholders.bankName') }),
          },
        }}
        render={({ field, fieldState: { error } }) => {
          return (
            <BaseInput
              {...field}
              label={t('form.placeholders.bankName')}
              placeholder={t('form.placeholders.bankName')}
              required
              error={error?.message}
            />
          );
        }}
      />
      <Controller
        name='bankCountry'
        control={control}
        rules={{ required: t('errors.form.required', { ns: 'common' }) }}
        render={({ field: { value, onChange }, fieldState: { error, isDirty } }) => {
          if (isDirty && error) clearErrors('bankCountry');

          return (
            <Select
              buttonClassName='bg-transparent'
              label={t('form.placeholders.bankCountry')}
              enableSearch
              nullValue={{ isEnable: true, text: t('form.placeholders.bankCountry') }}
              items={countryOptions}
              required
              error={error?.message}
              value={value}
              onChange={(e) => (onChange(e), setValue('purpose', ''), setValue('bankProvince', ''))}
            />
          );
        }}
      />
      <Controller
        name='accountNumber'
        control={control}
        rules={{
          required: !iban ? t('errors.form.required', { ns: 'common' }) : false,
          maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
          pattern: {
            value: ValidateUtils.alphaNumericRegex,
            message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('form.placeholders.accountNumber') }),
          },
        }}
        render={({ field, fieldState: { error } }) => (
          <BaseInput
            {...field}
            label={t('form.placeholders.accountNumber')}
            placeholder={t('form.placeholders.accountNumber')}
            required={!iban}
            error={error?.message}
          />
        )}
      />
      <Controller
        name='bic'
        control={control}
        rules={{
          required: t('errors.form.required', { ns: 'common' }),
          maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
          pattern: { value: ValidateUtils.bicCodeRegex, message: t('errors.form.bicSwift.invalidFormat', { ns: 'common' }) },
        }}
        render={({ field, fieldState: { error } }) => {
          return (
            <BaseInput
              {...field}
              label={t('form.placeholders.bicSwiftCode')}
              placeholder={t('form.placeholders.bicSwiftCode')}
              required
              error={error?.message}
            />
          );
        }}
      />
      {mandatoryFields.data.includes('insi_accounttype') && (
        <Controller
          name='accountType'
          control={control}
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Select
              buttonClassName='bg-transparent'
              label={t('form.placeholders.accountType')}
              nullValue={{ isEnable: true, text: t('form.placeholders.accountType') }}
              items={accountTypeOptions}
              required
              error={error?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />
      )}
      {mandatoryFields.data.includes('insi_bankprovince') && (
        <Controller
          control={control}
          name='bankProvince'
          rules={{ required: t('errors.form.required', { ns: 'common' }) }}
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <Select
              buttonClassName='bg-transparent'
              label={t('form.placeholders.bankProvinceRegion')}
              enableSearch
              nullValue={{ isEnable: true, text: t('form.placeholders.bankProvinceRegion') }}
              items={selectedBankProvinces}
              required
              error={error?.message}
              value={value}
              onChange={onChange}
            />
          )}
        />
      )}
      {mandatoryFields.data.includes('insi_bankaddress') && (
        <Controller
          control={control}
          name='bankAddress'
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
            pattern: {
              value: ValidateUtils.alphaNumericRegex,
              message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('form.placeholders.bankAddress') }),
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <BaseInput
              {...field}
              label={t('form.placeholders.bankAddress')}
              placeholder={t('form.placeholders.bankAddress')}
              required
              error={error?.message}
            />
          )}
        />
      )}
      {mandatoryFields.data.includes('insi_bankcity') && (
        <Controller
          control={control}
          name='bankCity'
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
            pattern: {
              value: ValidateUtils.alphaNumericRegex,
              message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('form.placeholders.bankCity') }),
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <BaseInput
              {...field}
              label={t('form.placeholders.bankCity')}
              placeholder={t('form.placeholders.bankCity')}
              required
              error={error?.message}
            />
          )}
        />
      )}
      {mandatoryFields.data.includes('insi_branchcode') && (
        <Controller
          control={control}
          name='branchCode'
          rules={{
            required: t('errors.form.required', { ns: 'common' }),
            maxLength: { value: 100, message: t('errors.form.maxCharacters', { ns: 'common', max: 100 }) },
            pattern: {
              value: ValidateUtils.alphaNumericRegex,
              message: t('errors.form.alphaNumericFormat', { ns: 'common', field: t('form.placeholders.branchCode') }),
            },
          }}
          render={({ field, fieldState: { error } }) => (
            <BaseInput
              {...field}
              label={t('form.placeholders.branchCode')}
              placeholder={t('form.placeholders.branchCode')}
              required
              error={error?.message}
            />
          )}
        />
      )}
    </div>
  );
};
